import React, { forwardRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useWizardStore } from "@companion-core/shared/app/Store/wizardStore";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { Field } from "@companion-core/shared/app/Interfaces/wizard";
import { SummaryProps } from ".";
import "@companion-core/web/src/Assets/Styles/scss/summary.scss";

const getSpokenValue = (field: Field): string => {
  if (!field.summaryValue) return "";
  if (field?.name?.includes("date") && field?.formattedSummaryDate) {
    return field.formattedSummaryDate;
  }
  return field.summaryValue;
};

const renderVisualValue = (field: Field) => {
  if (!field.summaryValue) return null;

  if (field?.name?.includes("date") && field?.value) {
    return <time dateTime={field.value as string}>{field.summaryValue}</time>;
  }

  return field.summaryValue;
};

export const Summary = forwardRef<HTMLElement, SummaryProps>(
  ({ fieldList, display = "mobile", "aria-labelledby": ariaLabelledBy }, ref) => {
    const { t } = useTranslation();
    const { bellContent, setHeaderAccessibilityLabel } = useBellStore((state) => ({
      bellContent: state.bellContent,
      setHeaderAccessibilityLabel: state.setHeaderAccessibilityLabel,
    }));
    let usedFields: Field[];
    if (fieldList) {
      usedFields = fieldList;
    } else {
      const { fields } = useWizardStore((state) => ({
        fields: state.fields,
      }));
      usedFields = fields;
    }

    const filteredFields = usedFields.filter(
      (field: Field) => !field.excludeFromSummary && field.summaryValue
    );

    useEffect(() => {
      if (bellContent !== "form") return;
      setHeaderAccessibilityLabel(
        t("request.concierge.header.accessibility.title.summary", {
          count: filteredFields.length,
        })
      );
    }, [bellContent, filteredFields.length, setHeaderAccessibilityLabel, t]);

    return (
      <section ref={ref} data-testid="summary" className="summary" aria-labelledby={ariaLabelledBy}>
        <dl className="summary__list">
          {filteredFields.map((field, index) => {
            const labelText = t(`${field.label}`);
            const spokenLabel = `${labelText}, ${getSpokenValue(field)}`;
            const valueClassName = `${
              display === "desktop" ? "summary__value--desktop" : "summary__value"
            } body-text-6`;

            return (
              <div
                key={index}
                data-testid={`summary-item-${field.name}`}
                className={`${display === "desktop" ? "summary__item--desktop" : "summary__item"}`}>
                <dt
                  aria-hidden={true}
                  data-testid={`summary-title-${field.name}`}
                  className={`${display === "desktop" ? "summary__label--desktop" : "summary__label"}`}>
                  {field.icon && (
                    <FontIcon
                      name={field.icon}
                      className="summary__label-icon"
                      size={display === "desktop" ? 20 : 14}
                      aria-hidden={true}
                    />
                  )}
                  <span
                    className={`body-text-5 ${
                      display === "desktop" ? "summary__label-title--desktop" : ""
                    }`}>
                    {labelText}
                  </span>
                </dt>
                <dd data-testid={`summary-value-${field.name}`} className={valueClassName}>
                  <span tabIndex={0} className="summary__focus-target">
                    <span className="summary__sr-only">{spokenLabel}</span>
                    <span aria-hidden={true}>{renderVisualValue(field)}</span>
                  </span>
                </dd>
              </div>
            );
          })}
        </dl>
      </section>
    );
  }
);

Summary.displayName = "Summary";
